import { fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import { PrismButton, PrismIcon } from '@mylighthouse/prism-ember';
import { includes } from '@nullvoxpopuli/ember-composable-helpers';
import { t } from 'ember-intl';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import { eq } from 'ember-truth-helpers';
import type { ColumnSettings } from 'lumen/utils/market-snapshots-column-settings';

import styles from './manage-columns.module.css';

export interface ManageColumnsSignature {
  Args: {
    /** Optional always-first column that can't be moved or hidden (e.g. OTB). */
    fixedLabel?: string;
    /** Display label per column id in `value.order`. */
    labels: Record<string, string>;
    onChange: (value: ColumnSettings) => void;
    value: ColumnSettings;
  };
  Element: HTMLUListElement;
}

export default class ManageColumns extends Component<ManageColumnsSignature> {
  groupName = guidFor(this);

  /** Without a fixed column, the last visible one can't be hidden. */
  get lastVisibleId(): string | undefined {
    if (this.args.fixedLabel) {
      return undefined;
    }

    const visible = this.args.value.order.filter(
      (id) => !this.args.value.hidden.includes(id),
    );

    return visible.length === 1 ? visible[0] : undefined;
  }

  @action
  onOrderChange(order: string[]): void {
    this.args.onChange({ ...this.args.value, order });
  }

  @action
  toggleHidden(id: string, event: Event): void {
    event.stopPropagation();
    const { hidden } = this.args.value;

    this.args.onChange({
      ...this.args.value,
      hidden: hidden.includes(id)
        ? hidden.filter((hiddenId) => hiddenId !== id)
        : [...hidden, id],
    });
  }

  <template>
    <ul
      class="text-300--regular {{styles.list}}"
      data-test-id="manage-columns"
      {{sortableGroup onChange=this.onOrderChange groupName=this.groupName}}
      ...attributes
    >
      {{#if @fixedLabel}}
        <li class={{styles.item}} data-test-id="manage-columns-fixed">
          <span class="flex-1 truncate">{{@fixedLabel}}</span>
        </li>
      {{/if}}
      {{#each @value.order key="@identity" as |id|}}
        {{#let (includes id @value.hidden) as |isHidden|}}
          <li
            class="{{styles.item}} {{styles.draggable}}"
            data-test-id="manage-columns-item"
            {{sortableItem model=id distance=5 groupName=this.groupName}}
          >
            <span aria-hidden="true" class={{styles.handle}}>
              <PrismIcon @name="drag" @size="small" />
            </span>
            <span
              class="flex-1 truncate"
              id="{{this.groupName}}-{{id}}-label"
            >{{get @labels id}}</span>
            <PrismButton
              @buttonType="ghost"
              @icon={{if isHidden "visibility-off" "visibility-on"}}
              @isDisabled={{eq id this.lastVisibleId}}
              @size="small"
              aria-describedby="{{this.groupName}}-{{id}}-label"
              aria-label={{t
                (if
                  isHidden
                  "market.snapshots.show_column"
                  "market.snapshots.hide_column"
                )
              }}
              data-test-id="manage-columns-toggle"
              {{on "click" (fn this.toggleHidden id)}}
            />
          </li>
        {{/let}}
      {{/each}}
    </ul>
  </template>
}
